import { CaretRight } from "@phosphor-icons/react";
import classNames from "classnames";
import * as React from "react";
import styles from "./Button.module.css";
import { lerp } from "../utils/animations";
import { LERP_FACTOR } from "../utils/constants";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "accent";
export type ButtonSize = "small" | "medium" | "large";

type BaseButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  showChevron?: boolean;
  darkMode?: boolean;
  className?: string;
};

export type ButtonProps = BaseButtonProps &
  (
    | (React.ButtonHTMLAttributes<HTMLButtonElement> & {
        as?: "button";
        href?: never;
      })
    | (React.AnchorHTMLAttributes<HTMLAnchorElement> & {
        as: "a";
        href: string;
      })
    | (React.HTMLAttributes<HTMLSpanElement> & {
        as: "span";
        href?: never;
      })
  );

const Chevron = () => (
  <span className={styles.chevronWrapper}>
    <CaretRight className={styles.chevronOut} size={14} weight="bold" />
    <CaretRight className={styles.chevronIn} size={14} weight="bold" />
  </span>
);

const Glow = () => (
  <div className={styles.glowContainer}>
    <div className={styles.glowWrapper}>
      <div className={styles.glowTracker}>
        <div className={styles.glowInner}>
          <div className={styles.glowBlob1} />
          <div className={styles.glowBlob2} />
        </div>
      </div>
    </div>
  </div>
);

export const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "medium",
  children,
  className,
  as = "button",
  href,
  showChevron = false,
  darkMode = false,
  ...props
}) => {
  const buttonRef = React.useRef<
    HTMLButtonElement | HTMLAnchorElement | HTMLSpanElement
  >(null);
  const targetPos = React.useRef({ x: 0, y: 0 });
  const outerPos = React.useRef({ x: 0, y: 0 });
  const currentScale = React.useRef(0.5);
  const animationRef = React.useRef<number | null>(null);
  const isHovering = React.useRef(false);
  const fillPhaseComplete = React.useRef(false);
  const fillStartTime = React.useRef<number | null>(null);
  const fillDuration = 400; // milliseconds for fill phase

  const animate = React.useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Phase 1: Fill the button with blob (scale grows to fill)
    // Phase 2: Once filled, track cursor position
    const now = Date.now();
    const fillElapsed = fillStartTime.current ? now - fillStartTime.current : 0;

    if (!fillPhaseComplete.current && fillElapsed < fillDuration) {
      // Phase 1: Grow scale to fill the button
      const fillProgress = Math.min(fillElapsed / fillDuration, 1);
      // Use easing for smoother fill animation
      const easedProgress =
        fillProgress < 0.5
          ? 2 * fillProgress * fillProgress
          : 1 - Math.pow(-2 * fillProgress + 2, 2) / 2;
      const fillTargetScale = 1.5; // Larger scale to ensure full coverage
      currentScale.current = lerp(0.2, fillTargetScale, easedProgress);

      // Keep position at center during fill phase
      outerPos.current.x = centerX;
      outerPos.current.y = centerY;

      // Check if fill phase is complete
      if (fillProgress >= 1) {
        fillPhaseComplete.current = true;
      }
    } else {
      // Phase 2: Track cursor position
      if (!fillPhaseComplete.current) {
        fillPhaseComplete.current = true;
      }

      outerPos.current.x = lerp(
        outerPos.current.x,
        targetPos.current.x,
        LERP_FACTOR.position,
      );
      outerPos.current.y = lerp(
        outerPos.current.y,
        targetPos.current.y,
        LERP_FACTOR.position,
      );

      // Calculate target scale based on position (smaller at edges, larger in center)
      const distanceFromCenter = Math.abs(outerPos.current.x - centerX);
      const maxDistance = rect.width / 2;
      const targetScale = 0.65 + (1 - distanceFromCenter / maxDistance) * 0.35;

      // Lerp scale to target (slightly slower for smooth transition)
      currentScale.current = lerp(
        currentScale.current,
        targetScale,
        LERP_FACTOR.scale,
      );
    }

    // Reduce the shift distance (only move 40% of the actual distance from center)
    const shiftX = (outerPos.current.x - centerX) * 0.4;
    const shiftY = (outerPos.current.y - centerY) * 0.3;

    buttonRef.current.style.setProperty("--mouse-x-outer", `${shiftX}px`);
    buttonRef.current.style.setProperty("--mouse-y-outer", `${shiftY}px`);
    buttonRef.current.style.setProperty(
      "--glow-scale",
      `${currentScale.current}`,
    );

    if (isHovering.current) {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [fillDuration]);

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    targetPos.current.x = e.clientX - rect.left;
    targetPos.current.y = e.clientY - rect.top;
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    isHovering.current = true;
    fillPhaseComplete.current = false; // Reset fill phase
    fillStartTime.current = Date.now(); // Start fill timer
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      // Initialize target to entry point
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      targetPos.current = { x, y };
      // Start glow position from center so it travels to cursor
      outerPos.current = { x: centerX, y: centerY };
      // Start scale small so blob fills the button first
      currentScale.current = 0.2;
    }
    animationRef.current = requestAnimationFrame(animate);
  };

  const handleMouseLeave = () => {
    isHovering.current = false;
    fillPhaseComplete.current = false; // Reset for next hover
    fillStartTime.current = null; // Reset fill timer
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  React.useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const baseClassName = classNames(
    styles.button,
    styles[variant],
    styles[size],
    darkMode && styles.darkMode,
    className,
  );

  if (as === "a" && href) {
    return (
      <a
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        className={baseClassName}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        <Glow />
        <span className={styles.content}>{children}</span>
        {showChevron && <Chevron />}
      </a>
    );
  }

  if (as === "span") {
    return (
      <span
        ref={buttonRef as React.RefObject<HTMLSpanElement>}
        className={baseClassName}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...(props as React.HTMLAttributes<HTMLSpanElement>)}
      >
        <Glow />
        <span className={styles.content}>{children}</span>
        {showChevron && <Chevron />}
      </span>
    );
  }

  return (
    <button
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      className={baseClassName}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      <Glow />
      <span className={styles.content}>{children}</span>
      {showChevron && <Chevron />}
    </button>
  );
};
